import { useDocumentOcr } from "@/hooks/use-ocr";
import type { OcrData, OcrResponse } from "@/types/OcrResponse";
import { AnimatePresence, motion } from "framer-motion"; // Corretto import
import { AlertCircle, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useDialogStore } from "./alert-dialog-store";
import AvatarUpload from "./avatar-upload";
import type { City } from "./city-select";
import type { Country } from "./country-select";
import Anagraphic from "./GuestFormSteps/Anagraphic";
import DocumentVerification from "./GuestFormSteps/DocumentVerification";
import Verification from "./GuestFormSteps/Verification";
import type { Province } from "./province-select";
import { Alert, AlertIcon, AlertTitle } from "./ui/alert";
import { Button } from "./ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Label } from "./ui/label";
import {
  Stepper,
  StepperContent,
  StepperIndicator,
  StepperItem,
  StepperNav,
  StepperPanel,
  StepperTitle,
  StepperTrigger,
} from "./ui/stepper";
export interface GuestDataProps {
  idRisPren?: string;
  pmsId?: number;
  crmId?: number;
  name: string;
  surname: string;
  email?: string;
  phoneNumber?: string; // Cambiato a string per gestire prefissi/zeri
  birthDate: Date | null;
  gender?: Gender | null;
  birthCountry?: Country;
  birthCity?: City;
  birthProvince?: Province;
  // Dati di residenza
  residenceCountry?: Country;
  residenceCity?: City;
  residenceAddress?: string;
  residenceStreetNumber?: string;
  residenceProvince?: Province;
  cap?: string;
  // Codice fiscale mostrato per Italia (sia nascita che residenza)
  taxCode?: string;
  frontDocumentData?: File;
  backDocumentData?: File;
  privacyPolicy?: boolean;
  marketingPolicy?: boolean;
  nationality?: string;
  documentNumber?: string;
  issuingDate?: Date | null;
  expirationDate?: Date | null;
  isDocumentExpired?: boolean;
  issuingCountry?: Country;
  issuingAuthority?: string;
  issuingCity?: City;
  documentType?: string;
  // Dati fattura
  invoiceRequest?: boolean;
  companyName?: string;
  vatNumber?: string;
  invoiceAddress?: string;
  invoiceCap?: string;
  invoiceCity?: string;
  recipientCode?: string;
  pecEmail?: string;
  ocrResponse?: OcrData;
  base64FrontData?: string;
  base64BackData?: string;
}
export enum Gender {
  Male = 0,
  Female = 1,
  Other = 2,
}
interface GuestFormProps {
  index: number;
  data: GuestDataProps;
  onChange: (updatedData: GuestDataProps) => void;
  onRemove?: (data: GuestDataProps) => void;
  onError?: (errors: string[]) => void;
}
const variants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 0 : 50,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction < 0 ? 50 : -50,
    opacity: 0,
  }),
};
export function GuestForm({
  index,
  data,
  onChange,
  onRemove,
  onError,
}: GuestFormProps) {
  const { t } = useTranslation("guestForm");
  const handleChange = (field: keyof GuestDataProps, value: any) => {
    onChange({ ...data, [field]: value });
  };

  const openDialog = useDialogStore((state) => state.openDialog);
  const [[step, direction], setStep] = useState([1, 0]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { processDocument, updateDocumentData } = useDocumentOcr();
  const [currentHasErrors, setCurrentHasErrors] = useState<boolean>(false);
  const extractDocumentData = async (back?: File, base64Back?: string) => {
    setIsLoading(true);
    setStep([3, 0]);
    processDocument(
      data?.frontDocumentData,
      (response: OcrResponse) => {
        const updatedData = updateDocumentData(
          data,
          response,
          back || ({} as File),
          base64Back || "",
        );
        onChange(updatedData);
        if (updatedData.isDocumentExpired) {
          toast.warning(
            t(
              "documentTab.docExpiredWarning",
              "Attenzione: il documento scansionato risulta scaduto. La data di scadenza non è stata inserita.",
            ),
            {
              richColors: true,
              duration: 6000,
              description: t(
                "documentTab.docExpiredWarningDesc",
                "Inserire un documento valido con data di scadenza futura.",
              ),
            },
          );
        }
        setIsLoading(false);
      },
      (file) => {
        onChange({
          ...data,
          backDocumentData: back,
          base64BackData: base64Back,
        });
        setIsLoading(false);
      },
      back,
    );
  };
  const handleHasErrors = (errors: string[]) => {
    if (errors.length > 0) {
      setCurrentHasErrors(true);
    } else {
      setCurrentHasErrors(false);
    }
  };

  // Funzione di validazione per un singolo ospite (uguale a quella del Check In)
  const isGuestValid = () => {
    // Campi sempre obbligatori
    if (!data.name || data.name.trim() === "") return false;
    if (!data.surname || data.surname.trim() === "") return false;
    if (!data.birthDate) return false;
    if (data.gender === undefined || data.gender === null) return false;
    if (!data.email || data.email.trim() === "") return false;
    if (!data?.birthCountry) return false;
    if (
      !data.birthCity ||
      !data.birthCity.name ||
      data.birthCity.name.trim() === ""
    )
      return false;

    // Validazioni per nascita italiana
    if (data.birthCountry?.iso2?.toUpperCase() === "IT") {
      if (!data.birthProvince || data.birthProvince.code.trim() === "")
        return false;
    }

    // Campi obbligatori di residenza
    if (!data.residenceCountry) return false;
    if (
      !data.residenceCity ||
      !data.residenceCity.name ||
      data.residenceCity.name.trim() === ""
    )
      return false;
    if (!data.residenceAddress || data.residenceAddress.trim() === "")
      return false;

    // Validazioni per residenza italiana
    if (data.residenceCountry?.iso2?.toUpperCase() === "IT") {
      if (!data.residenceProvince || data.residenceProvince.code.trim() === "")
        return false;

      if (!data.cap || data.cap.trim() === "") return false;
    }

    return true;
  };
  const getBase64 = async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      let reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = function () {
        return resolve(reader.result as string);
      };
      reader.onerror = function (error) {
        reject(error);
      };
    });
  };
  const steps = [
    {
      title: t("ui.documentFront"),
      component: (
        <>
          <div className="flex justify-center flex-col items-center gap-4">
            <Label className="text-2xl">{t("ui.documentFrontTitle")}</Label>
            <AvatarUpload
              maxSize={5 * 1024 * 1024}
              value={data?.frontDocumentData}
              label={t("ui.uploadFront")}
              onFileChange={async (documentProps) => {
                if (!documentProps?.file) return;
                if (documentProps?.file instanceof File === false) return;

                try {
                  const base = await getBase64(documentProps?.file);
                  onChange({
                    ...data,
                    frontDocumentData: documentProps?.file,
                    base64FrontData: base,
                  });
                  setStep([2, 0]);
                } catch (error) {}
              }}
            ></AvatarUpload>
            <Alert variant={"primary"}>
              <AlertIcon>
                <AlertCircle />
              </AlertIcon>
              <AlertTitle>
                {t("documentVerification.foreignNationalityWarning")}
              </AlertTitle>
            </Alert>
            <Button
              variant="secondary"
              className="cursor-pointer w-full"
              onClick={() => setStep([2, 0])}
            >
              {t("ui.skip")}
            </Button>
          </div>
        </>
      ),
    },
    {
      title: t("ui.documentBack"),
      disabled: !data?.frontDocumentData,
      component: (
        <>
          <div className="flex justify-center flex-col items-center gap-4">
            <Label className="text-2xl">{t("ui.documentBackTitle")}</Label>
            <AvatarUpload
              maxSize={5 * 1024 * 1024}
              value={data?.backDocumentData}
              label={t("ui.uploadBack")}
              onFileChange={async (documentProps) => {
                if (!documentProps?.file) return;
                if (documentProps.file instanceof File === false) return;

                try {
                  const base = await getBase64(documentProps?.file);
                  extractDocumentData(documentProps.file, base);
                } catch (error) {}
              }}
            ></AvatarUpload>
            <Alert variant={"primary"}>
              <AlertIcon>
                <AlertCircle />
              </AlertIcon>
              <AlertTitle>
                {t("documentVerification.foreignNationalityWarning")}
              </AlertTitle>
            </Alert>
            <Button
              variant="secondary"
              className="cursor-pointer w-full"
              onClick={() => {
                if (data?.frontDocumentData) {
                  extractDocumentData();
                } else {
                  setStep([3, 0]);
                }
              }}
            >
              {data?.frontDocumentData
                ? t("ui.skipAndExtract", "Salta e analizza fronte")
                : t("ui.skip")}
            </Button>
          </div>
        </>
      ),
    },
    {
      title: t("ui.documentData"),
      disabled: !data?.frontDocumentData,
      component: (
        <DocumentVerification
          data={data}
          loading={isLoading}
          onChange={onChange}
          onError={handleHasErrors}
          onNext={() => setStep([step + 1, 0])}
        />
      ),
    },
    {
      title: t("ui.anagraphicData"),
      disabled: !data?.frontDocumentData,
      component: (
        <Anagraphic
          data={data}
          loading={isLoading}
          onChange={onChange}
          onError={onError}
          onNext={() => setStep([step + 1, 0])}
        />
      ),
    },
    {
      title: t("ui.verification"),
      disabled: !data?.frontDocumentData,
      component: (
        <Verification
          data={data}
          loading={isLoading}
          onChange={onChange}
          onVerifyUser={(userId: string | null, fromPms: boolean) => {
            if (fromPms) {
              handleChange("pmsId", userId);
            } else {
              handleChange("crmId", userId);
            }
          }}
        />
      ),
    },
  ];

  return (
    <Card className="w-full mb-4">
      <CardHeader>
        <CardTitle className="text-lg">
          {t("ui.guest")} {index + 1}
        </CardTitle>
        <CardAction>
          <Button
            variant="ghost"
            className="text-destructive hover:text-red-800"
            size={"icon-sm"}
            onClick={() => onRemove && onRemove(data)}
          >
            <Trash2 />
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent>
        <Stepper defaultValue={1} value={step} className="space-y-8">
          <StepperNav className="gap-1 mb-15">
            {steps.map((step, index) => {
              return (
                <StepperItem
                  key={index}
                  step={index + 1}
                  disabled={step.disabled || currentHasErrors}
                  className="relative flex-1 items-start"
                >
                  <StepperTrigger
                    onClick={() => setStep([index + 1, 1])}
                    className="flex flex-col items-start justify-center gap-3.5 grow"
                  >
                    <StepperIndicator className="transition-colors duration-500 bg-border h-1.5 w-full data-[state=active]:bg-primary"></StepperIndicator>
                    <div className="flex flex-col items-start gap-1">
                      <StepperTitle className="text-start font-semibold group-data-[state=inactive]/step:text-muted-foreground">
                        {step.title}
                      </StepperTitle>
                    </div>
                  </StepperTrigger>
                </StepperItem>
              );
            })}
          </StepperNav>
          <StepperPanel className="relative min-h-[100px]">
            <AnimatePresence mode="wait" custom={direction}>
              {steps.map((step, index) => (
                <motion.div
                  key={index + 1}
                  custom={direction}
                  variants={variants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{
                    x: { type: "spring", stiffness: 300, damping: 30 },
                    opacity: { duration: 0.2 },
                  }}
                  className="w-full"
                >
                  <StepperContent value={index + 1}>
                    {step.component}
                  </StepperContent>
                </motion.div>
              ))}
            </AnimatePresence>
          </StepperPanel>
        </Stepper>
      </CardContent>
    </Card>
  );
}
